import { DatePickerState } from '@/types/admin/createEvent';
import { useState, useCallback } from 'react';
import { Platform, Alert } from 'react-native';
import { showAlert } from '@/utils/common/alert';

// Proper DateTimePicker types
interface DateTimePickerEvent {
  type: 'set' | 'dismissed' | 'neutralButtonPressed';
  nativeEvent: {
    timestamp?: number;
    utcOffset?: number;
  };
}

type DateTimePickerComponent = React.ComponentType<{
  value: Date;
  mode?: 'date' | 'time' | 'datetime' | 'countdown';
  display?: 'default' | 'spinner' | 'compact' | 'inline';
  onChange?: (event: DateTimePickerEvent, selectedDate?: Date) => void;
  minimumDate?: Date;
  maximumDate?: Date;
  timeZoneOffsetInMinutes?: number;
  timeZoneOffsetInSeconds?: number;
  dayOfWeekFormat?: string;
  dateFormat?: string;
  firstDayOfWeek?: number;
  textColor?: string;
  accentColor?: string;
  themeVariant?: 'light' | 'dark';
  locale?: string;
  is24Hour?: boolean;
  neutralButtonLabel?: string;
  positiveButtonLabel?: string;
  negativeButtonLabel?: string;
}>;

// Conditional import to handle potential linking issues
let DateTimePicker: DateTimePickerComponent | null;
try {
  DateTimePicker = require('@react-native-community/datetimepicker').default;
} catch (error) {
  console.warn('DateTimePicker not available, using fallback');
  DateTimePicker = null;
}

export const useDatePicker = () => {
  const [datePickerState, setDatePickerState] = useState<DatePickerState>({
    showStartDatePicker: false,
    showEndDatePicker: false,
    showPublishDatePicker: false,
    datePickerMode: 'date',
    tempDate: new Date(),
  });

  const handleDatePickerChange = useCallback((event: DateTimePickerEvent, selectedDate?: Date) => {
    if (Platform.OS === 'android') {
      setDatePickerState(prev => ({
        ...prev,
        showStartDatePicker: false,
        showEndDatePicker: false,
        showPublishDatePicker: false,
      }));
    }
    
    if (selectedDate) {
      setDatePickerState(prev => ({ ...prev, tempDate: selectedDate }));
    }
  }, []);

  const showStartDatePicker = useCallback((currentDate: Date) => {
    setDatePickerState(prev => ({
      ...prev,
      tempDate: currentDate,
      showStartDatePicker: true,
      datePickerMode: 'date',
    }));
  }, []);

  const showEndDatePicker = useCallback((currentDate: Date) => {
    setDatePickerState(prev => ({
      ...prev,
      tempDate: currentDate,
      showEndDatePicker: true,
      datePickerMode: 'date',
    }));
  }, []);

  const showPublishDatePicker = useCallback((currentDate: Date) => {
    setDatePickerState(prev => ({
      ...prev,
      tempDate: currentDate,
      showPublishDatePicker: true,
      datePickerMode: 'date',
    }));
  }, []);

  const hideDatePickers = useCallback(() => {
    setDatePickerState(prev => ({
      ...prev,
      showStartDatePicker: false,
      showEndDatePicker: false,
      showPublishDatePicker: false,
      datePickerMode: 'date',
    }));
  }, []);

  const confirmDateSelection = useCallback((
    pickerType: 'start' | 'end' | 'publish',
    onConfirm: (date: Date, type: 'start' | 'end' | 'publish') => void
  ) => {
    onConfirm(datePickerState.tempDate, pickerType);
    hideDatePickers();
  }, [datePickerState.tempDate, hideDatePickers]);

  const showFallbackDatePicker = useCallback((
    title: string,
    currentValue: Date,
    onConfirm: (date: Date) => void
  ) => {
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    Alert.prompt(
      title,
      'Enter date and time (YYYY-MM-DD HH:MM)',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'OK',
          onPress: (input?: string) => {
            if (input) {
              const parsedDate = new Date(input);
              if (!isNaN(parsedDate.getTime())) {
                onConfirm(parsedDate);
              } else {
                showAlert('Invalid Date', 'Please enter a valid date in YYYY-MM-DD HH:MM format');
              }
            }
          },
        },
      ],
      'plain-text',
      formatDate(currentValue)
    );
  }, []);

  const setDatePickerMode = useCallback((mode: 'date' | 'time') => {
    setDatePickerState(prev => ({ ...prev, datePickerMode: mode }));
  }, []);

  return {
    datePickerState,
    DateTimePicker,
    handleDatePickerChange,
    showStartDatePicker,
    showEndDatePicker,
    showPublishDatePicker,
    hideDatePickers,
    confirmDateSelection,
    showFallbackDatePicker,
    setDatePickerMode,
  };
};
